import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Phone, QrCode, Car, CheckCircle, AlertTriangle, Gift, Shield } from "lucide-react";
import axios from "axios";
import { KioskViewport } from "../../components/ui/kiosk-viewport";
import { KioskHeader } from "../../components/ui/kiosk-header";
import { toast } from "sonner";

const API = '/api';
const IDLE_TIMEOUT = 60;

/**
 * KioskPayQREntry - QR-specific VRM entry screen
 * 
 * FLOW:
 * 1. Customer enters VRM
 * 2. GET /api/parking/session/{vrm}/calculate
 * 3. Display session info + amount due
 * 4. If payment required -> navigate to KioskPayQR with state
 * 5. If no payment needed -> show appropriate message
 * 
 * MUST NOT:
 * - Call /api/terminal/intent
 * - Redirect to /kiosk/pay-kiosk
 */
export default function KioskPayQREntry() {
  const navigate = useNavigate();
  const [vehicleReg, setVehicleReg] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const idleTimerRef = useRef(null);
  const inputRef = useRef(null);

  // Idle timeout - return to attract after 60s inactivity
  useEffect(() => {
    const resetIdleTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => navigate('/kiosk/attract'), IDLE_TIMEOUT * 1000);
    };
    const events = ['mousedown', 'touchstart', 'keydown'];
    events.forEach(e => document.addEventListener(e, resetIdleTimer, { passive: true }));
    resetIdleTimer();
    return () => {
      events.forEach(e => document.removeEventListener(e, resetIdleTimer));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [navigate]);

  // Focus input on mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleKeyPress = (key) => {
    if (key === 'BACK') {
      setVehicleReg(prev => prev.slice(0, -1));
    } else if (key === 'CLEAR') {
      setVehicleReg('');
      setSessionData(null);
      setError(null);
    } else if (key === 'SPACE') {
      if (vehicleReg.length < 10) {
        setVehicleReg(prev => prev + ' ');
      }
    } else if (vehicleReg.length < 10) {
      setVehicleReg(prev => prev + key);
    }
  };

  const handleCheckSession = async () => {
    if (!vehicleReg || vehicleReg.trim().length < 2) {
      setError("Please enter a valid vehicle registration");
      return;
    }
    
    setLoading(true);
    setError(null);
    setSessionData(null);
    
    const vrm = vehicleReg.trim().toUpperCase();
    
    try {
      const response = await axios.get(`${API}/parking/session/${encodeURIComponent(vrm)}/calculate`);
      
      if (response.data && response.data.session_id) {
        setSessionData({
          session_id: response.data.session_id,
          vehicle_registration: response.data.vehicle_registration,
          duration_display: response.data.duration_display,
          amount_due: response.data.amount_due,
          is_grace_period: response.data.is_grace_period,
          has_entitlement: response.data.has_entitlement,
          entitlement_discount: response.data.entitlement_discount,
          is_whitelisted: response.data.is_whitelisted ?? false,
          whitelist_reason: response.data.whitelist_reason ?? null,
          has_exit_auth: response.data.has_exit_auth ?? false,
          is_settled: response.data.is_settled ?? false
        });
      } else {
        setError("Unexpected response from server.");
      }
    } catch (err) {
      if (err.response?.status === 404) {
        // Genuine no-session outcome
        setError("Free parking for you today.");
      } else {
        // API/network error
        setError(err.response?.data?.detail || "Unable to check fee. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleProceedToQR = () => {
    if (!sessionData || !sessionData.session_id) return;
    
    // Navigate to KioskPayQR with required state
    navigate("/kiosk/pay-qr", {
      state: {
        sessionId: sessionData.session_id,
        vehicleReg: sessionData.vehicle_registration,
        amount: Math.round(sessionData.amount_due * 100), // Convert to pence for Stripe
        durationDisplay: sessionData.duration_display
      }
    });
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '£0.00';
    return `£${parseFloat(amount).toFixed(2)}`;
  };

  // Determine if payment is actually required
  const paymentRequired = sessionData && 
    sessionData.amount_due > 0 && 
    !sessionData.is_grace_period && 
    !sessionData.is_whitelisted &&
    !sessionData.has_exit_auth &&
    !sessionData.is_settled;

  // Determine reason for no payment
  const getNoPaymentReason = () => {
    if (!sessionData) return null;
    if (sessionData.is_whitelisted) return { type: 'whitelist', message: sessionData.whitelist_reason || 'Whitelisted Vehicle' };
    if (sessionData.is_grace_period) return { type: 'grace', message: 'Within free parking period' };
    if (sessionData.has_exit_auth) return { type: 'paid', message: 'Already authorised to exit' };
    if (sessionData.is_settled) return { type: 'paid', message: 'Already paid' };
    if (sessionData.amount_due <= 0) return { type: 'free', message: 'No charge due' };
    return null;
  };

  const noPaymentReason = getNoPaymentReason();

  return (
    <KioskViewport>
      <div 
        data-testid="kiosk-pay-qr-entry"
        style={{ 
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: "'Outfit', 'Inter', system-ui, sans-serif",
          overflow: 'hidden',
          background: '#0d2137'
        }}
      >
        <KioskHeader showDateTime={true} />

        {/* MAIN CONTENT */}
        <main style={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column',
          background: 'linear-gradient(180deg, #e0f2fe 0%, #bae6fd 50%, #7dd3fc 100%)', 
          padding: '24px 32px'
        }}>
          {/* Back Button */}
          <button 
            onClick={() => navigate("/kiosk")}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '14px 24px',
              borderRadius: '14px',
              backgroundColor: '#1e3a5f',
              border: 'none',
              cursor: 'pointer',
              alignSelf: 'flex-start',
              marginBottom: '20px'
            }}
            data-testid="back-button"
          >
            <ArrowLeft size={24} color="white" />
            <span style={{ color: 'white', fontWeight: '700', fontSize: '20px' }}>Back</span>
          </button>

          {/* Title with QR Icon */}
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', marginBottom: '8px' }}>
              <QrCode size={48} color="#7c3aed" />
              <h1 style={{ fontSize: '42px', fontWeight: '700', color: '#0f172a', margin: 0 }}>
                Pay by QR Code
              </h1>
            </div>
            <p style={{ fontSize: '22px', color: '#475569' }}>
              Enter your vehicle registration to check your parking fee
            </p>
          </div>

          {/* Main Content Area */}
          <div style={{ 
            flex: 1, 
            display: 'flex', 
            gap: '24px',
            minHeight: 0
          }}>
            {/* Left Side - VRM Entry */}
            <div style={{ 
              flex: 1, 
              display: 'flex', 
              flexDirection: 'column',
              background: 'white',
              borderRadius: '24px',
              padding: '24px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
            }}>
              {/* VRM Display */}
              <div style={{
                background: '#fef9c3',
                border: '4px solid #1e3a5f',
                borderRadius: '16px',
                padding: '20px',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '16px'
              }}>
                <div style={{
                  background: '#1e40af',
                  color: 'white',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  fontSize: '20px',
                  fontWeight: '700'
                }}>
                  GB
                </div>
                <input
                  ref={inputRef}
                  type="text"
                  value={vehicleReg}
                  onChange={(e) => setVehicleReg(e.target.value.toUpperCase())}
                  placeholder="ENTER REG"
                  style={{
                    flex: 1,
                    fontSize: '48px',
                    fontWeight: '700',
                    fontFamily: "'UK Number Plate', 'Courier New', monospace",
                    letterSpacing: '4px',
                    background: 'transparent',
                    border: 'none',
                    outline: 'none',
                    textAlign: 'center',
                    color: '#0f172a'
                  }}
                  data-testid="vrm-input"
                />
              </div>

              {/* Virtual Keyboard */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
                {['1234567890', 'QWERTYUIOP', 'ASDFGHJKL', 'ZXCVBNM'].map((row, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                    {row.split('').map(key => (
                      <button
                        key={key}
                        onClick={() => handleKeyPress(key)}
                        style={{
                          width: '64px',
                          height: '56px',
                          fontSize: '24px',
                          fontWeight: '700',
                          background: '#f1f5f9',
                          border: '2px solid #cbd5e1',
                          borderRadius: '10px',
                          cursor: 'pointer'
                        }}
                      >
                        {key}
                      </button>
                    ))}
                  </div>
                ))}
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginTop: '8px' }}>
                  <button
                    onClick={() => handleKeyPress('CLEAR')}
                    style={{
                      padding: '14px 28px',
                      fontSize: '18px',
                      fontWeight: '700',
                      background: '#fee2e2',
                      color: '#dc2626',
                      border: '2px solid #fca5a5',
                      borderRadius: '10px',
                      cursor: 'pointer'
                    }}
                  >
                    CLEAR
                  </button>
                  <button
                    onClick={() => handleKeyPress('SPACE')}
                    style={{
                      padding: '14px 48px',
                      fontSize: '18px',
                      fontWeight: '700',
                      background: '#f1f5f9',
                      border: '2px solid #cbd5e1',
                      borderRadius: '10px',
                      cursor: 'pointer'
                    }}
                  >
                    SPACE
                  </button>
                  <button
                    onClick={() => handleKeyPress('BACK')}
                    style={{
                      padding: '14px 28px',
                      fontSize: '18px',
                      fontWeight: '700',
                      background: '#fef3c7',
                      color: '#d97706',
                      border: '2px solid #fcd34d',
                      borderRadius: '10px',
                      cursor: 'pointer'
                    }}
                  >
                    ⌫ DELETE
                  </button>
                </div>
              </div>

              {/* Check Fee Button */}
              <button
                onClick={handleCheckSession}
                disabled={loading || vehicleReg.trim().length < 2}
                style={{
                  marginTop: '16px',
                  padding: '20px',
                  fontSize: '26px',
                  fontWeight: '700',
                  background: loading || vehicleReg.trim().length < 2 
                    ? '#94a3b8' 
                    : 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '16px',
                  cursor: loading || vehicleReg.trim().length < 2 ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  boxShadow: '0 8px 24px rgba(124, 58, 237, 0.3)'
                }}
                data-testid="check-fee-button"
              >
                {loading ? (
                  <>
                    <Loader2 size={28} style={{ animation: 'spin 1s linear infinite' }} />
                    Checking...
                  </>
                ) : (
                  <>
                    <Car size={28} />
                    CHECK PARKING FEE
                  </>
                )}
              </button>
            </div>

            {/* Right Side - Session Result */}
            <div style={{ 
              flex: 1, 
              display: 'flex', 
              flexDirection: 'column',
              background: 'white',
              borderRadius: '24px',
              padding: '24px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
            }}>
              {/* Error State */}
              {error && !sessionData && (
                <div style={{ 
                  flex: 1, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  textAlign: 'center',
                  padding: '32px'
                }} data-testid="error-state">
                  {error === "Free parking for you today." ? (
                    <>
                      <CheckCircle size={100} color="#22c55e" style={{ marginBottom: '24px' }} />
                      <h2 style={{ fontSize: '36px', fontWeight: '700', color: '#22c55e', marginBottom: '12px' }}>
                        Free parking for you today.
                      </h2>
                      <p style={{ fontSize: '22px', color: '#64748b', marginBottom: '32px' }}>
                        No active parking session found for this vehicle.
                      </p>
                    </>
                  ) : (
                    <>
                      <AlertTriangle size={100} color="#f59e0b" style={{ marginBottom: '24px' }} />
                      <h2 style={{ fontSize: '32px', fontWeight: '700', color: '#dc2626', marginBottom: '12px' }}>
                        Unable to Find Session
                      </h2>
                      <p style={{ fontSize: '20px', color: '#64748b' }}>{error}</p>
                    </>
                  )}
                </div>
              )}

              {/* Session Found - Payment Required */}
              {sessionData && paymentRequired && (
                <div style={{ 
                  flex: 1, 
                  display: 'flex', 
                  flexDirection: 'column',
                  gap: '20px'
                }} data-testid="session-payment-required">
                  {/* Session Header */}
                  <div style={{
                    background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                    borderRadius: '16px',
                    padding: '20px',
                    textAlign: 'center',
                    border: '2px solid #93c5fd'
                  }}>
                    <p style={{ fontSize: '18px', color: '#1e40af', marginBottom: '4px' }}>Vehicle</p>
                    <p style={{ fontSize: '36px', fontWeight: '700', color: '#1e3a5f', margin: 0, letterSpacing: '2px' }}>
                      {sessionData.vehicle_registration}
                    </p>
                  </div>

                  {/* Duration */}
                  <div style={{
                    background: '#f8fafc',
                    borderRadius: '12px',
                    padding: '16px',
                    textAlign: 'center'
                  }}>
                    <p style={{ fontSize: '16px', color: '#64748b', marginBottom: '4px' }}>Parking Duration</p>
                    <p style={{ fontSize: '28px', fontWeight: '600', color: '#0f172a', margin: 0 }}>
                      {sessionData.duration_display || 'Calculating...'}
                    </p>
                  </div>

                  {/* Amount Due */}
                  <div style={{
                    background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
                    borderRadius: '16px',
                    padding: '24px',
                    textAlign: 'center',
                    border: '3px solid #86efac',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}>
                    <p style={{ fontSize: '20px', color: '#166534', marginBottom: '8px' }}>Amount to Pay</p>
                    <p style={{ fontSize: '72px', fontWeight: '800', color: '#15803d', margin: 0 }}>
                      {formatCurrency(sessionData.amount_due)}
                    </p>
                    {sessionData.has_entitlement && sessionData.entitlement_discount > 0 && (
                      <div style={{ 
                        marginTop: '12px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        gap: '8px',
                        color: '#15803d'
                      }}>
                        <Gift size={20} />
                        <span style={{ fontSize: '16px', fontWeight: '600' }}>
                          Includes £{sessionData.entitlement_discount.toFixed(2)} Local Connect discount
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Proceed to QR Button */}
                  <button
                    onClick={handleProceedToQR}
                    style={{
                      padding: '24px',
                      fontSize: '28px',
                      fontWeight: '700',
                      background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '16px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '16px',
                      boxShadow: '0 12px 32px rgba(124, 58, 237, 0.4)'
                    }}
                    data-testid="proceed-to-qr-button"
                  >
                    <QrCode size={36} />
                    GENERATE QR CODE
                  </button>
                </div>
              )}

              {/* Session Found - No Payment Required */}
              {sessionData && !paymentRequired && noPaymentReason && (
                <div style={{ 
                  flex: 1, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  textAlign: 'center',
                  padding: '32px'
                }} data-testid="no-payment-required">
                  {noPaymentReason.type === 'whitelist' && (
                    <>
                      <Shield size={100} color="#3b82f6" style={{ marginBottom: '24px' }} />
                      <h2 style={{ fontSize: '36px', fontWeight: '700', color: '#3b82f6', marginBottom: '12px' }}>
                        Whitelisted Vehicle
                      </h2>
                    </>
                  )}
                  {noPaymentReason.type === 'grace' && (
                    <>
                      <CheckCircle size={100} color="#22c55e" style={{ marginBottom: '24px' }} />
                      <h2 style={{ fontSize: '36px', fontWeight: '700', color: '#22c55e', marginBottom: '12px' }}>
                        Free Parking Period
                      </h2>
                    </>
                  )}
                  {(noPaymentReason.type === 'paid' || noPaymentReason.type === 'free') && (
                    <>
                      <CheckCircle size={100} color="#22c55e" style={{ marginBottom: '24px' }} />
                      <h2 style={{ fontSize: '36px', fontWeight: '700', color: '#22c55e', marginBottom: '12px' }}>
                        No Payment Required
                      </h2>
                    </>
                  )}
                  <p style={{ fontSize: '24px', color: '#64748b', marginBottom: '16px' }}>
                    {noPaymentReason.message}
                  </p>
                  <p style={{ fontSize: '28px', fontWeight: '700', color: '#0f172a', marginBottom: '32px' }}>
                    {sessionData.vehicle_registration}
                  </p>
                  <p style={{ fontSize: '22px', color: '#16a34a', fontWeight: '600' }}>
                    You are free to exit the car park.
                  </p>
                </div>
              )}

              {/* Initial State - No Session Yet */}
              {!sessionData && !error && !loading && (
                <div style={{ 
                  flex: 1, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  textAlign: 'center',
                  padding: '32px',
                  color: '#94a3b8'
                }} data-testid="initial-state">
                  <Car size={80} style={{ marginBottom: '24px', opacity: 0.5 }} />
                  <p style={{ fontSize: '24px' }}>
                    Enter your vehicle registration and tap CHECK PARKING FEE
                  </p>
                </div>
              )}

              {/* Loading State */}
              {loading && (
                <div style={{ 
                  flex: 1, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center'
                }} data-testid="loading-state">
                  <Loader2 size={80} color="#7c3aed" style={{ animation: 'spin 1s linear infinite', marginBottom: '24px' }} />
                  <p style={{ fontSize: '24px', color: '#7c3aed', fontWeight: '600' }}>
                    Looking up your parking session...
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Help Footer */}
          <div style={{ 
            marginTop: '20px',
            padding: '20px 32px',
            background: '#0d2137',
            borderRadius: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '16px'
          }}>
            <Phone size={28} color="#fbbf24" />
            <span style={{ color: 'white', fontWeight: '600', fontSize: '20px' }}>Need help? Call </span>
            <span style={{ color: '#fbbf24', fontWeight: '700', fontSize: '26px' }}>01292 833107</span>
          </div>
        </main>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </KioskViewport>
  );
}
